import { isEqual } from 'lodash';

import { whiteLabelStore } from 'Models/WhiteLabelStore';
import { appConfigStore } from 'Models/AppConfigStore';
import { BookingCreationAdditionalOption, FerryBookingCreationDto } from 'Models/Dtos/BookingDtos';
import { TowOnTypeEntity } from 'Models/Entities';
import { getMeasurementValueFromId } from 'Util/_HumanWritten/MeasurementUtils';
import passengerTypeStore from 'Models/PassengerTypeStore';
import { IBookingFormGroupHeaders } from 'Hooks/useGroupHeaders';
import { isNullOrUndefined } from 'Util/TypeGuards';
import { normaliseSingleDriver } from 'Util/_HumanWritten/DriverAssignment';
import { convertWeight, parseWeightUnit, toCanonicalKg } from 'Util/Weight';
import { BookingAddOn } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BookingAddOn';
import { TabSelected } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardTypes';
import { parseDynamicFieldValues } from 'Stores/DynamicBookingFieldsStore';
import { CheckInBookingOverviewDto } from '../CheckInEntities/CheckInBookingOverviewDto';
import { IBookingFormState } from '../context/BookingFormState';

/**
 * IMPORTANT: This links an external button to a form element in BookingFormEdit component.
 */
export const BOOKING_FORM_ID = 'booking-form-id';

/**
 * IMPORTANT: This links an external button to a form element in BookingFormEdit component.
 */
export const CLOSE_BOOKING_FORM_ID = 'booking-form-id-onClose';

/**
 * Ensure `booking` has all the dependencies to produce FerryBookingCreationDto.
 *
 * Please see function implementation to see deconstruction of `booking`.
 */
export function transformBookingToBookingCreationDto(booking: CheckInBookingOverviewDto): FerryBookingCreationDto {
	const {
		id: bookingId,
		user: { id: userId, firstName, lastName, phone },
		bookedSummary: {
			ferryTrip: { id: tripId },
			adultPassengerTickets,
			childPassengerTickets,
			infantPassengerTickets,
			driverFirstName,
			driverLastName,
			driverPhone,
			cargoInfo,
			towOnInfo,
			freightDetail,
			additionalBookingOptions,
			passengerDTickets,
			passengerETickets,
			passengerFTickets,
			passengerGTickets,
			passengerHTickets,
			note,
			alterationContext,
		},
		returnBooking,
	} = booking;

	let tabSelected = 'passenger';
	if (cargoInfo) {
		tabSelected = 'vehicle';
	} else if (freightDetail) {
		tabSelected = 'freight';
	}

	if (isNullOrUndefined(bookingId)) {
		tabSelected = whiteLabelStore.defaultBookingType as TabSelected;
	}

	const displayWeightUnit = appConfigStore.weightDisplayUnit;
	const vehicleExactInDisplayUnit =
		cargoInfo?.exactWeight != null
			? convertWeight(
					Number(cargoInfo.exactWeight),
					parseWeightUnit(cargoInfo.exactWeightUnit, displayWeightUnit),
					displayWeightUnit,
				)
			: null;
	const trailerExactInDisplayUnit =
		towOnInfo?.exactWeight != null
			? convertWeight(
					Number(towOnInfo.exactWeight),
					parseWeightUnit(towOnInfo.exactWeightUnit, displayWeightUnit),
					displayWeightUnit,
				)
			: null;

	const dto: FerryBookingCreationDto = {
		inCheckIn: true,
		type: tabSelected as TabSelected,
		bookingToEdit: bookingId,
		userId,
		departureTicketId: tripId,
		returningTicketId: returnBooking?.bookedSummary?.ferryTrip.id,
		adultTickets: adultPassengerTickets,
		childTickets: childPassengerTickets,
		infantTickets: infantPassengerTickets,
		passengerDTickets: passengerDTickets,
		passengerETickets: passengerETickets,
		passengerFTickets: passengerFTickets,
		passengerGTickets: passengerGTickets,
		passengerHTickets: passengerHTickets,
		driverFirstName: cargoInfo ? driverFirstName : firstName,
		driverLastName: cargoInfo ? driverLastName : lastName,
		driverPhone: cargoInfo ? driverPhone : phone,
		cargoIdentification: cargoInfo?.cargoIdentification,
		hiredVehicle: cargoInfo?.tbc,
		cargoTypeId: cargoInfo?.cargoTypeId,
		vehicleLengthId: cargoInfo?.selectedLengthId,
		vehicleWeightId: cargoInfo?.selectedWeightId,
		vehicleExactWeight: vehicleExactInDisplayUnit,
		vehicleExactWeightUnit: vehicleExactInDisplayUnit !== null ? displayWeightUnit : null,
		trailerTypeId: towOnInfo?.towOnTypeId,
		trailerLengthId: towOnInfo?.selectedLengthId,
		trailerWidthId: towOnInfo?.selectedWidthId,
		trailerExactWeight: trailerExactInDisplayUnit,
		trailerExactWeightUnit: trailerExactInDisplayUnit !== null ? displayWeightUnit : null,
		departingTripOptions: additionalBookingOptions.map(
			x => ({ optionId: x.option.id, amount: x.quantity }) as BookingAddOn,
		),
		returningTripOptions:
			returnBooking?.bookedSummary?.additionalBookingOptions.map(
				x => ({ optionId: x.option.id, amount: x.quantity }) as BookingAddOn,
			) ?? [],
		bypassSpaceCheck: true,
		note,
		location: 'CHECK_IN',
		cargoCharacteristics: [...(alterationContext?.cargoCharacteristics ?? [])],
		trailerCargoCharacteristics: [...(alterationContext?.trailerCargoCharacteristics ?? [])],
		removedInheritedCharacteristics: [...(alterationContext?.removedInheritedCharacteristics ?? [])],
		// Hydrate admin-configured dynamic field values from the alteration so they
		// pre-fill when editing, and persist back on save.
		dynamicFieldValues: Object.fromEntries(
			parseDynamicFieldValues((alterationContext as any)?.dynamicFieldValues).map(e => [e.fieldId, e.value]),
		),

		// Freight specific
		freightCategoryId: freightDetail?.freightCategoryId,
		freightTypeId: freightDetail?.freightTypeId,
		quantity: freightDetail?.quantity,
		description: freightDetail?.description,
		length: freightDetail?.length,
		width: freightDetail?.width,
		height: freightDetail?.height,
		weight: freightDetail?.weight,
		handlingIds: freightDetail?.freightHandlings?.map(x => x.freightHandlingId),

		// Add departingJourney if departing trip is available
		...(booking.bookedSummary && {
			departingJourney: {
				tripId: booking.bookedSummary.ferryTrip.id,
				startStopId: booking.bookedSummary.startStopId,
				endStopId: booking.bookedSummary.endStopId,
			},
		}),

		// Add returningJourney if returning trip is available
		...(returnBooking &&
			returnBooking.bookedSummary && {
				returningJourney: {
					tripId: returnBooking.bookedSummary.ferryTrip.id,
					startStopId: returnBooking.bookedSummary.startStopId,
					endStopId: returnBooking.bookedSummary.endStopId,
				},
			}),
	};

	// A booking saved with the driver flag on two passenger types cannot be repaired from the
	// counters — "Set as driver" is hidden for a type that already holds the flag, so neither of the
	// two can be cleared. Both `initialState` and the live `formState` are built from here, so the
	// repair itself does not read as an edit.
	normaliseSingleDriver(dto);
	return dto;
}

export function transformBookingToBookingFormState(booking: CheckInBookingOverviewDto): IBookingFormState {
	const {
		user: { email },
		bookedSummary: { cargoInfo, towOnInfo, freightDetail },
	} = booking;

	return {
		...transformBookingToBookingCreationDto(booking),
		email: email,
		cargoMake: cargoInfo?.cargoType?.cargoMake ?? '',
		cargoModel: cargoInfo?.cargoType?.cargoModel ?? '',
		vehicleLengthId: cargoInfo?.selectedLengthId,
		trailerLengthId: towOnInfo?.selectedLengthId,
		vehicleWidthId: cargoInfo?.selectedWidthId,
		user: booking.user,
		giftCertificateUsages: [],
		transactionFees: [],
		forEftpos: true,
		// Freight
		freightTypeId: freightDetail?.freightTypeId,
		freightCategoryId: freightDetail?.freightCategoryId,
		handlingIds: freightDetail?.freightHandlings?.map(x => x.freightHandlingId),
		description: freightDetail?.description,
		quantity: freightDetail?.quantity,
		length: freightDetail?.length,
		width: freightDetail?.width,
		height: freightDetail?.height,
		// Receiver details
		receiverFirstName: freightDetail?.freightReceiverContact?.firstName,
		receiverLastName: freightDetail?.freightReceiverContact?.lastName,
		receiverPhone: freightDetail?.freightReceiverContact?.phone,
		receiverEmail: freightDetail?.freightReceiverContact?.email,
		receiverCompanyName: freightDetail?.freightReceiverContact?.companyName,
		// Notify details
		notifyFirstName: freightDetail?.freightNotifyContact?.firstName,
		notifyLastName: freightDetail?.freightNotifyContact?.lastName,
		notifyPhone: freightDetail?.freightNotifyContact?.phone,
		notifyEmail: freightDetail?.freightNotifyContact?.email,
		notifyCompanyName: freightDetail?.freightNotifyContact?.companyName,
	};
}

/**
 * Returns true if any differences have been detected between add-ons in both actual and expected lists.
 * False otherwise.
 */
export function compareAddOnOptions(
	expected: BookingCreationAdditionalOption[],
	actual: BookingCreationAdditionalOption[],
): boolean {
	if (expected.length !== actual.length) {
		return true;
	}

	const expectedIds = expected.map(x => x.optionId);
	const actualIds = actual.map(x => x.optionId);
	if (expectedIds.filter(x => actualIds.includes(x)).length !== expected.length) {
		return true;
	}

	return expected.some(x => {
		return x.amount !== actual.find(y => y.optionId === x.optionId)?.amount;
	});
}

/**
 * Returns true when a property of initial and current state does not match. Returns false otherwise.
 * @param initialState The booking form state before any changes.
 * @param currentState The booking form state after any changes.
 */
/**
 * Fields that take no part in change detection: they either duplicate something already compared
 * via its id (`user` ↔ `userId`), or are metadata about the edit rather than edited values.
 */
const ignoredChangeKeys: (keyof IBookingFormState)[] = [
	'giftCertificateUsages',
	'returningTripOptions',
	'bookingToEdit',
	'user',
	// Only ever assigned to the live form state (see `CheckInContext.setFormState`), never to the
	// freshly-built initial state, so it would always register as a change.
	'bookingBeingEdited',
];

export function bookingFormHasChanged(initialState: IBookingFormState, currentState: IBookingFormState) {
	const hasChanged = (Object.keys(currentState) as (keyof IBookingFormState)[]).some(currentKey => {
		if (ignoredChangeKeys.includes(currentKey)) {
			return false;
		}
		if (currentKey === 'departingTripOptions') {
			// Add-ons need comparing per option id rather than as whole lists, because the amounts
			// are what change. Returns true if a change is detected.
			return compareAddOnOptions(
				currentState.departingTripOptions ?? [],
				initialState.departingTripOptions ?? [],
			);
		}
		// Compared structurally, not by reference. Many fields hold objects or arrays (the eight
		// passenger-ticket lists, the journeys, the cargo characteristic lists, …) and `initialState`
		// is always a freshly constructed `BookingFormState`, so a reference comparison reported
		// every one of them as changed and made this function return true unconditionally.
		return !isEqual(currentState[currentKey], initialState[currentKey]);
	});

	return hasChanged;
}

/**
 * Returns true if vehicle or towOn values of both states are not the same. Returns false otherwise.
 */
export function groupHeaderHasChanged(initialState: IBookingFormGroupHeaders, currentState: IBookingFormGroupHeaders) {
	const hasChanged =
		initialState.vehicles.show !== currentState.vehicles.show ||
		initialState.trailers.show !== currentState.trailers.show ||
		initialState.addOns.show !== currentState.addOns.show;

	return hasChanged;
}

/** Returns the vehicle length. */
export function getVehicleLength(x: IBookingFormState) {
	if (x.vehicleLengthId !== undefined && x.vehicleLengthId !== '') {
		return getMeasurementValueFromId(x.vehicleLengthId);
	}
	return 0;
}

/** Returns the trailer length. */
export function getTrailerLength(x: IBookingFormState) {
	if (x.trailerLengthId !== undefined && x.trailerLengthId !== '') {
		return getMeasurementValueFromId(x.trailerLengthId);
	}
	return 0;
}

/**
 * Returns the total weight (kg) the booking will occupy on the ferry,
 * combining vehicle + trailer contributions. Prefers customer-entered exact
 * weights over selected weight bands; mirrors the server's
 * WeightCapacityCalculator so the pre-check value matches what the server
 * uses for live capacity tracking.
 */
export function getWeight(x: IBookingFormState, towOnTypes?: TowOnTypeEntity[]): number {
	let total = 0;

	// Vehicle: exact weight (converted to kg) wins over the selected band.
	if (x.vehicleExactWeight != null && x.vehicleExactWeightUnit) {
		total += toCanonicalKg(Number(x.vehicleExactWeight), parseWeightUnit(x.vehicleExactWeightUnit, 'kg'));
	} else if (x.vehicleWeightId) {
		total += getMeasurementValueFromId(x.vehicleWeightId) ?? 0;
	}

	// Trailer: exact weight wins; otherwise fall back to the tow-on type's
	// minimum weight measurement, then to the global minimum.
	const hasTrailer = !!x.trailerTypeId || !!x.trailerLengthId;
	if (hasTrailer) {
		if (x.trailerExactWeight != null && x.trailerExactWeightUnit) {
			total += toCanonicalKg(Number(x.trailerExactWeight), parseWeightUnit(x.trailerExactWeightUnit, 'kg'));
		} else {
			const towOnType = towOnTypes?.find(t => t.id === x.trailerTypeId);
			if (towOnType?.minimumWeightId) {
				total += getMeasurementValueFromId(towOnType.minimumWeightId) ?? 0;
			}
		}
	}

	return total;
}

export function getPassengerCount(x: IBookingFormState): number {
	return passengerTypeStore.getTotalFromBookingDto(x);
}
